/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { Button, CircularProgress, DialogActions, FilledInput, FormControl, Grid, IconButton, InputLabel, Typography } from '@mui/material';
import { CancelPresentationRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { calculateFeaturedWellboreVolume } from '../../../../../../store/actions/hydraulicsLanding';
import { enqueueSnackbar } from '../../../../../../store/actions/snackbar';
import ModalDialog from '../../../../../ui/ModalDialog';
import { isMobile } from 'react-device-detect';

const WellboreVolumeCal = ({
  onCloseClick,
  isOpen,
  runId,
  properties
}) => {
  const classes = styles();
  const dispatch = useDispatch();
  const [fromMD, setFromMD] = useState('');
  const [toMD, setToMD] = useState('');
  const [wellboreVolume, setWellboreVolume] = useState({});
  const hydraulicRunUnits = useSelector((state) => state.hydraulicsLanding?.units?.hydraulicsRun);
  const units = hydraulicRunUnits?.results?.depth_points;
  const { depths, volumes } = wellboreVolume?.data?.detail?.results || {};
  const { depths: depthUnits, volumes: volumeUnits } = wellboreVolume?.data?.detail?.units || {};
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (loader) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loader]);

  const closeHandler = () => {
    onCloseClick();
    setWellboreVolume({});
  };

  useEffect(() => {
    setFromMD(0);
    setToMD(properties?.bit_depth);
  }, [isOpen]);

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
        {isMobile
          ? (
          <Typography fontSize='18px' fontWeight='bold'>Wellbore Volume Calculator</Typography>
            )
          : (
        <Grid item>
          {'Wellbore Volume Calculator'}
        </Grid>
            )}
        <Grid item>
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='large' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const checkValues = () => {
    const from = Number(fromMD);
    const to = Number(toMD);

    if (fromMD === '' || toMD === '') {
      dispatch(
        enqueueSnackbar(
          'Please enter both From MD and To MD values.',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }

    const maxDepth = properties?.hole_depth;

    if (from < 0 || from > maxDepth || to < 0 || to > maxDepth) {
      dispatch(
        enqueueSnackbar(
          `From MD and To MD values must be between 0 and ${maxDepth}.`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
    return true;
  };

  const calculateVolume = () => {
    if (checkValues()) {
      const postBody = {
        fromMD: Number(fromMD),
        toMD: Number(toMD),
        runId
      };
      setLoader(true);
      dispatch(calculateFeaturedWellboreVolume(postBody, setWellboreVolume, setLoader));
    }
  };

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={classes.dialogModal}
    >
      <Grid marginTop="15px" marginBottom="1.2rem">
        <Grid
          container
          spacing={2}
          display="flex"
          justifyContent="center"
          alignItems="flex-start"
        >
          <Grid
            item
            xs={5}
            md={3}
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
          >
            <Typography
              variant="subtitle1"
              className={classes.labelHeader}
            >
              {`From MD (${units?.md?.label})`}
            </Typography>
            <FormControl
              fullWidth
              variant="filled"
              className={classes.inputField}
              sx={{ bgcolor: 'transparent' }}
            >
              <InputLabel htmlFor="outlined-adornment-from-md">
                Enter From MD
              </InputLabel>
              <FilledInput
                id="outlined-adornment-from-md"
                value={fromMD}
                disableUnderline
                disabled={loader}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^-?\d*\.?\d*$/.test(value)) {
                    setFromMD(value);
                  }
                  setWellboreVolume({});
                }}
              />
            </FormControl>
          </Grid>

          <Grid
            item
            xs={5}
            md={3}
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
          >
            <Typography
              variant="subtitle1"
              className={classes.labelHeader}
            >
              {`To MD (${units?.md?.label})`}
            </Typography>
            <FormControl
              fullWidth
              variant="filled"
              className={classes.inputField}
              sx={{ bgcolor: 'transparent' }}
            >
              <InputLabel htmlFor="outlined-adornment-to-md">
                Enter To MD
              </InputLabel>
              <FilledInput
                id="outlined-adornment-to-md"
                value={toMD}
                disableUnderline
                disabled={loader}
                onChange={(e) => {
                  setWellboreVolume({});
                  const value = e.target.value;
                  if (/^-?\d*\.?\d*$/.test(value)) {
                    setToMD(value);
                  }
                }}
              />
            </FormControl>
          </Grid>
        </Grid>

        {!wellboreVolume?.data?.detail?.results && (
          <Grid
            container
            item
            justifyContent="center"
            marginBottom="1rem"
            marginTop="1rem"
          >
            <DialogActions>
              <Button
                disabled={loader}
                sx={{ backgroundColor: '#444', width: '180px', opacity: loader ? 0.5 : 1 }}
                onClick={calculateVolume}
              >
                Calculate Volumes
              </Button>
              {loader && <CircularProgress sx={{ ml: 2 }} size='20px' />}
            </DialogActions>
          </Grid>
        )}

        {wellboreVolume?.data?.detail?.results && (
          <Grid
            container
            item
            direction='column'
            alignItems='flex-start'
            sx={{
              marginTop: isMobile ? '10%' : '1rem',
              width: 'fit-content',
              marginLeft: isMobile ? '30px' : 'calc(50% - 24%)',
            }}
          >
            <Typography variant="button" className={classes.result}>
              Hole Depth:{' '}
              {depths?.hole_depth?.toFixed(depthUnits?.hole_depth?.decimals) ?? '-'}{' '}
              {depthUnits?.hole_depth?.label}
            </Typography>

            <Typography
              variant="button"
              className={classes.result}
              sx={{ marginBottom: '12px' }}
            >
              Bit Depth:{' '}
              {depths?.bit_depth?.toFixed(depthUnits?.bit_depth?.decimals) ?? '-'}{' '}
              {depthUnits?.bit_depth?.label}
            </Typography>

            <Typography variant="button" className={classes.result}>
              Pipe Mud Vol:{' '}
              {volumes?.pipe_vol?.toFixed(volumeUnits?.pipe_vol?.decimals) ?? '-'}{' '}
              {volumeUnits?.pipe_vol?.label}
            </Typography>
            <Typography variant="button" className={classes.result}>
              Ann. Mud Vol:{' '}
              {volumes?.ann_vol?.toFixed(volumeUnits?.ann_vol?.decimals) ?? '-'}{' '}
              {volumeUnits?.ann_vol?.label}
            </Typography>
            <Typography variant="button" className={classes.result}>
              Total. Mud Vol:{' '}
              {(volumes?.pipe_vol + volumes?.ann_vol)?.toFixed(volumeUnits?.ann_vol?.decimals) ?? '-'}{' '}
              {volumeUnits?.ann_vol?.label}
            </Typography>
            <Typography variant="button" className={classes.result}>
              Pipe Metal Vol:{' '}
              {volumes?.metal_vol?.toFixed(volumeUnits?.metal_vol?.decimals) ?? '-'}{' '}
              {volumeUnits?.metal_vol?.label}
            </Typography>
          </Grid>
        )}
      </Grid>
    </ModalDialog>
  );
};
WellboreVolumeCal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  runId: PropTypes.any,
  properties: PropTypes.any
};

export default WellboreVolumeCal;
